import React, { lazy, Suspense, useContext } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { Helmet } from "react-helmet";
import Loading from 'components/shared-components/Loading';
import { APP_PREFIX_PATH, APP_NAME } from 'configs/AppConfig'
import { CheckPer } from 'hooks/checkPermission';
import { UserContext } from "hooks/UserContextProvider";

const Program = lazy(() => import(`./programs`));
const ProgramSubject = lazy(() => import('./program-subject'));
const Section = lazy(() => import(`./section`));
const Student = lazy(() => import(`./student`));
const Teacher = lazy(() => import(`./teacher`));
const TeacherPermission = lazy(() => import('./teacher/permissions'));
const School = lazy(() => import(`./school`));
const SubSchool = lazy(() => import(`./sub_school`));
const Classes = lazy(() => import(`./classes`));
const Mark = lazy(() => import(`./mark`));
const Subject = lazy(() => import(`./subject`));
const OnlineLesson = lazy(() => import(`./online-lesson`));
const OnlineFile = lazy(() => import(`./online-file`));
const Config = lazy(() => import(`./web-configs`));
const Parent = lazy(() => import(`./parent`));
const TestLib = lazy(() => import('./online-test-library'));
const Question = lazy(() => import('./questions'));
const TakeTest = lazy(() => import('./take-test'));
const TakeTestDetail = lazy(() => import('./take-test/detail'));
const Exam = lazy(() => import('./take-test/exam'));
const ShowScore = lazy(() => import('./take-test/show-score'));
const Routine = lazy(() => import(`./routine`));
const MarkBoard = lazy(() => import(`./mark-board`));
const StudentReport = lazy(() => import(`./student-report`));
const UpdatePassword = lazy(() => import('./user-settings/update-password'));
const UpdateProfile = lazy(() => import('./user-settings/update-profile'));
const Live = lazy(() => import('./live'));
const LiveMeeting = lazy(() => import('./live/live'))
const MarkConsolidation = lazy(() => import('./mark-consolidation'));
const Event = lazy(() => import('./event'));
const EmployeesAttendance = lazy(() => import('./employees-attendance'));
const Employees = lazy(() => import('./employees'));
const Support = lazy(() => import('./support'))
const Conversation = lazy(() => import('./conversation'))
const Assessment = lazy(() => import('./assessment'))
const Report = lazy(() => import('./report'))
const News = lazy(() => import('./news'))
const NewsDetail = lazy(() => import('./news/news-detail'))
const NewsForm = lazy(() => import('./news/form'))
const Recommendation = lazy(() => import('./recommendation'))
const RecommendationDetail = lazy(() => import('./recommendation/recommendation-detail'))
const RecommendationForm = lazy(() => import('./recommendation/form'))
const SchoolNews = lazy(() => import('./school-news'))
const SchoolNewsDetail = lazy(() => import('./school-news/school-news-detail'))
const SchoolNewsForm = lazy(() => import('./school-news/form'))
const VisaSupport = lazy(() => import('./visa-support'))
const VisaSupportDetail = lazy(() => import('./visa-support/visa-support-detail'))
const VisaSupportForm = lazy(() => import('./visa-support/form'))
const Samshil = lazy(() => import('./samshil'))
const SamshilDetail = lazy(() => import('./samshil/samshil-detail'))
const SamshilForm = lazy(() => import('./samshil/form'))

export const AppViews = (currentTitle) => {

    const title = currentTitle.currentTitle;

    const { user } = useContext(UserContext)

    if (user.isStudent) {
        if (user.student.isPaid === false) {
            return <Suspense fallback={<Loading cover="content" />}>
                <Switch>
                    <Route
                        path={`${APP_PREFIX_PATH}/payment`}
                        component={lazy(() => import(`./payment`))}
                    />
                    <Redirect from={`${APP_PREFIX_PATH}`} to={`${APP_PREFIX_PATH}/payment`} />
                </Switch>
            </Suspense>
        }
    }


    return (
        <>
            <Helmet>
                <title>{APP_NAME}</title>
            </Helmet>
            <Suspense fallback={<Loading cover="content" />}>
                <Switch>
                    <Route
                        path={`${APP_PREFIX_PATH}/home`}
                        component={lazy(() => import(`./home`))}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/support`}
                        render={props => <Support {...props} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/conversation`}
                        render={props => <Conversation {...props} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/report`}
                        render={props => <Report {...props} />}
                    />
                    {CheckPer('view_student') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/student`}
                            render={props => <Student {...props}
                                title={title} />}
                        />
                    }
                    <Route
                        path={`${APP_PREFIX_PATH}/teacher/permissions`}
                        render={props => <TeacherPermission {...props}
                        />}
                    />
                    {CheckPer('view_teacher') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/teacher`}
                            render={props => <Teacher {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_school') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/school`}
                            render={props => <School {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_sub_school') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/sub-school`}
                            render={props => <SubSchool {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_classes') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/classes`}
                            render={props => <Classes {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_program_subject') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/programs/:program`}
                            render={props => <ProgramSubject {...props}
                                title={`Хөтөлбөрийн хичээл`} />}
                        />
                    }
                    {CheckPer('view_program') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/programs`}
                            render={props => <Program {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_section') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/section`}
                            render={props => <Section {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_mark') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/mark`}
                            render={props => <Mark {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_subject') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/subject`}
                            render={props => <Subject {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_online_file') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/online-file`}
                            render={props => <OnlineFile {...props}
                                title={title} />}
                        />
                    }
                    <Route
                        path={`${APP_PREFIX_PATH}/news/create`}
                        render={props => <NewsForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/news/:id/edit`}
                        render={props => <NewsForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/news/:id`}
                        render={props => <NewsDetail {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/news`}
                        render={props => <News {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/recommendation/create`}
                        render={props => <RecommendationForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/recommendation/:id/edit`}
                        render={props => <RecommendationForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/recommendation/:id`}
                        render={props => <RecommendationDetail {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/recommendation`}
                        render={props => <Recommendation {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/school-news/create`}
                        render={props => <SchoolNewsForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/school-news/:id/edit`}
                        render={props => <SchoolNewsForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/school-news/:id`}
                        render={props => <SchoolNewsDetail {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/school-news`}
                        render={props => <SchoolNews {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/visa-support/create`}
                        render={props => <VisaSupportForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/visa-support/:id/edit`}
                        render={props => <VisaSupportForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/visa-support/:id`}
                        render={props => <VisaSupportDetail {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/visa-support`}
                        render={props => <VisaSupport {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/samshil/create`}
                        render={props => <SamshilForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/samshil/:id/edit`}
                        render={props => <SamshilForm {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/samshil/:id`}
                        render={props => <SamshilDetail {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/samshil`}
                        render={props => <Samshil {...props}
                            title={title} />}
                    />
                    {CheckPer('view_online_lesson') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/online-lesson`}
                            render={props => <OnlineLesson {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_parent') &&
                        <Route
                            path={`${APP_PREFIX_PATH}/parent`}
                            render={props => <Parent {...props}
                                title={title} />}
                        />
                    }
                    {CheckPer('view_routine') &&
                        <Route
                            title={title}
                            path={`${APP_PREFIX_PATH}/routine`}
                            render={props => <Routine {...props} title={title} />}
                        />
                    }
                    <Route
                        title={title}
                        path={`${APP_PREFIX_PATH}/assessment`}
                        render={props => <Assessment {...props} title={title} />}
                    />
                    {CheckPer('view_mark_board') &&
                        <Route
                            title={title}
                            path={`${APP_PREFIX_PATH}/mark-board`}
                            render={props => <MarkBoard {...props} title={title} />}
                        />
                    }
                    {
                        <Route
                            title={title}
                            path={`${APP_PREFIX_PATH}/mark-consolidation`}
                            render={props => <MarkConsolidation {...props} title={title} />}
                        />
                    }
                    <Route
                        title={title}
                        path={`${APP_PREFIX_PATH}/employees-attendance`}
                        render={props => <EmployeesAttendance {...props} title={title} />}
                    />
                    <Route
                        title={title}
                        path={`${APP_PREFIX_PATH}/employees`}
                        render={props => <Employees {...props} title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/student-report`}
                        render={props => <StudentReport {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/live/:meetingId`}
                        render={props => <LiveMeeting props={props} title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/live`}
                        render={props => <Live {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/my-score`}
                        render={props => <ShowScore {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/online-test-library/:test`}
                        render={props => <Question {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/online-test-library`}
                        render={props => <TestLib {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/take-test/:test`}
                        render={props => <TakeTestDetail {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/take-test`}
                        render={props => <TakeTest {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/exam/:test`}
                        render={props => <Exam {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/update-profile`}
                        render={props => <UpdateProfile {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/update-password`}
                        render={props => <UpdatePassword {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/configs`}
                        render={props => <Config {...props}
                            title={title} />}
                    />
                    <Route
                        path={`${APP_PREFIX_PATH}/event`}
                        render={props => <Event {...props}
                            title={title} />}
                    />
                    <Redirect from={`${APP_PREFIX_PATH}`} to={`${APP_PREFIX_PATH}/home`} />
                </Switch>
            </Suspense>
        </>
    )
}

export default React.memo(AppViews);